/**
 * EventCard Component
 *
 * Detail popup shown when clicking an event marker.
 * Each event type has a specific layout showing relevant information.
 *
 * Event Types:
 * - Spawn Inoculation: LC source, qty/format/mass, phase dates
 * - Substrate Inoculation: Spawn source, qty/format/mass, phase dates
 * - Induction: qty/format/mass, harvest estimate, phase dates
 * - First-Fourth Flush: harvested/yield/cumulative, interval dates
 * - Spent: total harvest/final yield/flushes, full lifespan
 *
 * Positioning:
 * - Anchored to the clicked event marker with callout arrow
 * - Flips above target if near bottom of viewport
 * - Horizontal centering with bounds checking
 */

import React, { useRef, useLayoutEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ExternalLink, Circle, CheckCircle2, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toLocalDateString } from '@/utils/dateUtils'

// Event type to display name mapping
const EVENT_NAMES = {
  spawn_inoculated: 'Spawn Inoculation',
  substrate_inoculated: 'Substrate Inoculation',
  transferred: 'Transfer to Fruiting',
  induction: 'Primordia Induction',
  first_flush: 'First Flush',
  second_flush: 'Second Flush',
  third_flush: 'Third Flush',
  fourth_flush: 'Fourth Flush',
  spent: 'Batch Complete'
}

/**
 * Format date as "10 Jan 26"
 */
function formatDateShort(date) {
  if (!date) return '—'
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: '2-digit'
  })
}

/**
 * Calculate days between two dates
 */
function daysBetween(date1, date2) {
  if (!date1 || !date2) return null
  const oneDay = 24 * 60 * 60 * 1000
  return Math.round(Math.abs(date2 - date1) / oneDay)
}

/**
 * Three-column row with headers (for Qty/Format/Mass etc.)
 */
function ThreeColumnRow({ headers, values }) {
  return (
    <div className="border-t border-border pt-3 mt-3">
      <div className="grid grid-cols-3 gap-2 text-center">
        {headers.map((header, i) => (
          <div key={i} className="text-xs text-muted-foreground">{header}</div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center mt-1">
        {values.map((value, i) => (
          <div key={i} className="text-sm font-medium">{value}</div>
        ))}
      </div>
    </div>
  )
}

/**
 * Single row with label and value
 */
function SingleRow({ label, value }) {
  return (
    <div className="border-t border-border pt-3 mt-3">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{value}</span>
      </div>
    </div>
  )
}

/**
 * Phase duration footer (Start | Days | End)
 */
function PhaseDurationFooter({ startDate, endDate }) {
  const days = daysBetween(startDate, endDate)
  return (
    <div className="border-t border-border pt-3 mt-3">
      <div className="grid grid-cols-3 gap-2 text-center text-sm">
        <div>{formatDateShort(startDate)}</div>
        <div className="text-muted-foreground">{days ? `${days} days` : '—'}</div>
        <div>{formatDateShort(endDate)}</div>
      </div>
    </div>
  )
}

/**
 * Clickable event header that links to tasks page
 */
function EventHeader({ eventType, eventDate, batch, onNavigate }) {
  const eventName = EVENT_NAMES[eventType] || eventType
  const handleClick = () => {
    // Format date as YYYY-MM-DD for URL param
    const dateParam = eventDate ? toLocalDateString(eventDate) : ''
    const url = dateParam
      ? `/tasks?batch=${batch.id}&date=${dateParam}`
      : `/tasks?batch=${batch.id}`
    onNavigate?.(url)
  }

  return (
    <button
      onClick={handleClick}
      className="text-sm font-semibold text-primary hover:underline flex items-center gap-1 mb-2"
    >
      {eventName}
      <ExternalLink className="w-3 h-3" />
    </button>
  )
}

/**
 * Task list section (shown when tasks are available)
 */
function TaskListSection({ tasks }) {
  if (!tasks || tasks.length === 0) return null

  return (
    <div className="border-t border-border pt-3 mt-3">
      <p className="text-xs font-medium text-muted-foreground mb-2">Tasks</p>
      <ul className="space-y-1.5">
        {tasks.map((task, idx) => (
          <li key={task.id || idx} className="flex items-center gap-2 text-xs">
            {task.status === 'completed' ? (
              <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
            ) : task.status === 'skipped' ? (
              <XCircle className="w-3 h-3 text-gray-400 shrink-0" />
            ) : (
              <Circle className="w-3 h-3 text-muted-foreground shrink-0" />
            )}
            <span className={cn(
              "truncate",
              task.status === 'completed' && "line-through text-muted-foreground"
            )}>
              {task.name}
            </span>
            {task.estimatedMinutes && (
              <span className="text-muted-foreground shrink-0 ml-auto">
                {task.estimatedMinutes}m
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Spawn Inoculation content
 */
function SpawnInoculationContent({ event, batch, onNavigate }) {
  return (
    <>
      {/* Clickable event header */}
      <EventHeader eventType={event.type} eventDate={event.date} batch={batch} onNavigate={onNavigate} />

      {/* Header: Strain + Date */}
      <div className="flex justify-between items-start">
        <div>
          <div className="font-semibold text-base">{batch.strain}</div>
          <div className="text-sm text-muted-foreground">{batch.id}</div>
          {event.lcSource ? (
            <button
              className="text-sm mt-1 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
              onClick={() => onNavigate?.(`/production/lc?select=${event.lcSource}`)}
            >
              {event.lcSource}
            </button>
          ) : (
            <div className="text-sm mt-1">—</div>
          )}
        </div>
        <div className="text-sm text-right">{formatDateShort(event.date)}</div>
      </div>

      {/* Qty / Format / Mass */}
      <ThreeColumnRow
        headers={['Qty', 'Format', 'Mass']}
        values={[
          event.quantity ? `${event.quantity}` : '—',
          event.format || '—',
          event.mass ? `${Math.round(event.mass)}KG` : '—'
        ]}
      />

      {/* Expected yield - show actual harvest if available, otherwise BE estimate */}
      {(event.harvestEstimate || event.harvested || batch.totalBlocks) && (
        <SingleRow
          label="Expected yield"
          value={
            event.harvested
              ? `${event.harvested}KG (actual)`
              : event.harvestEstimate
                ? `${Math.round(event.harvestEstimate * 10) / 10}KG (BE ${event.yieldPercent || batch.bePercent || 30}%)`
                : '—'
          }
        />
      )}

      {/* Task list (if tasks available) */}
      <TaskListSection tasks={event.tasks} />

      {/* Phase duration */}
      <PhaseDurationFooter
        startDate={event.date}
        endDate={event.phaseEndDate}
      />
    </>
  )
}

/**
 * Substrate Inoculation content
 */
function SubstrateInoculationContent({ event, batch, onNavigate }) {
  return (
    <>
      {/* Clickable event header */}
      <EventHeader eventType={event.type} eventDate={event.date} batch={batch} onNavigate={onNavigate} />

      {/* Header: Strain + Date */}
      <div className="flex justify-between items-start">
        <div>
          <div className="font-semibold text-base">{batch.strain}</div>
          <div className="text-sm text-muted-foreground">{batch.id}</div>
          <div className="text-sm mt-1">{event.spawnSource || '—'}</div>
        </div>
        <div className="text-sm text-right">{formatDateShort(event.date)}</div>
      </div>

      {/* Qty / Format / Mass */}
      <ThreeColumnRow
        headers={['Qty', 'Format', 'Mass']}
        values={[
          event.quantity ? `${event.quantity}` : '—',
          event.format || '—',
          event.mass ? `${event.mass}KG` : '—'
        ]}
      />

      {/* Task list (if tasks available) */}
      <TaskListSection tasks={event.tasks} />

      {/* Phase duration */}
      <PhaseDurationFooter
        startDate={event.date}
        endDate={event.phaseEndDate}
      />
    </>
  )
}

/**
 * Induction content (formerly Transferred)
 */
function InductionContent({ event, batch, onNavigate }) {
  return (
    <>
      {/* Clickable event header */}
      <EventHeader eventType={event.type} eventDate={event.date} batch={batch} onNavigate={onNavigate} />

      {/* Header: Strain + Date */}
      <div className="flex justify-between items-start">
        <div>
          <div className="font-semibold text-base">{batch.strain}</div>
          <div className="text-sm text-muted-foreground">{batch.id}</div>
        </div>
        <div className="text-sm text-right">{formatDateShort(event.date)}</div>
      </div>

      {/* Qty / Format / Mass */}
      <ThreeColumnRow
        headers={['Qty', 'Format', 'Mass']}
        values={[
          event.quantity ? `${event.quantity}` : '—',
          event.format || '—',
          event.mass ? `${event.mass}KG` : '—'
        ]}
      />

      {/* Harvest estimate */}
      <SingleRow
        label="Harvest estimate"
        value={event.harvestEstimate ? `${event.harvestEstimate}KG` : '—'}
      />

      {/* Task list (if tasks available) */}
      <TaskListSection tasks={event.tasks} />

      {/* Phase duration */}
      <PhaseDurationFooter
        startDate={event.date}
        endDate={event.phaseEndDate}
      />
    </>
  )
}

/**
 * Flush content (First, Second, Third, Fourth)
 */
function FlushContent({ event, batch, onNavigate }) {
  // Map event type to flush label
  const flushLabels = {
    first_flush: 'First Flush',
    second_flush: 'Second Flush',
    third_flush: 'Third Flush',
    fourth_flush: 'Fourth Flush'
  }
  const flushLabel = flushLabels[event.type] || 'Harvested'

  return (
    <>
      {/* Clickable event header */}
      <EventHeader eventType={event.type} eventDate={event.date} batch={batch} onNavigate={onNavigate} />

      {/* Header: Strain + Date */}
      <div className="flex justify-between items-start">
        <div>
          <div className="font-semibold text-base">{batch.strain}</div>
          <div className="text-sm text-muted-foreground">{batch.id}</div>
        </div>
        <div className="text-sm text-right">{formatDateShort(event.date)}</div>
      </div>

      {/* Flush harvest / Yield / Cumulative */}
      <ThreeColumnRow
        headers={[flushLabel, 'Yield', 'Cumulative']}
        values={[
          event.harvested ? `${event.harvested}KG` : '—',
          event.yieldPercent ? `${event.yieldPercent}%` : '—',
          event.cumulative ? `${event.cumulative}KG` : '—'
        ]}
      />

      {/* Task list (if tasks available) */}
      <TaskListSection tasks={event.tasks} />

      {/* Phase duration (interval to next flush) */}
      <PhaseDurationFooter
        startDate={event.date}
        endDate={event.phaseEndDate}
      />
    </>
  )
}

/**
 * Spent content
 */
function SpentContent({ event, batch, onNavigate }) {
  return (
    <>
      {/* Clickable event header */}
      <EventHeader eventType={event.type} eventDate={event.date} batch={batch} onNavigate={onNavigate} />

      {/* Header: Strain + Date */}
      <div className="flex justify-between items-start">
        <div>
          <div className="font-semibold text-base">{batch.strain}</div>
          <div className="text-sm text-muted-foreground">{batch.id}</div>
        </div>
        <div className="text-sm text-right">{formatDateShort(event.date)}</div>
      </div>

      {/* Total Harvest / Final Yield / Flushes */}
      <ThreeColumnRow
        headers={['Total Harvest', 'Final Yield', 'Flushes']}
        values={[
          event.totalHarvest ? `${event.totalHarvest}KG` : '—',
          event.finalYield ? `${event.finalYield}%` : '—',
          event.flushCount ? `${event.flushCount}` : '—'
        ]}
      />

      {/* Task list (if tasks available) */}
      <TaskListSection tasks={event.tasks} />

      {/* Full batch lifespan */}
      <PhaseDurationFooter
        startDate={event.batchStartDate}
        endDate={event.date}
      />
    </>
  )
}

/**
 * Get content component for event type
 */
function getEventContent(eventType) {
  switch (eventType) {
    case 'spawn_inoculated':
      return SpawnInoculationContent
    case 'substrate_inoculated':
      return SubstrateInoculationContent
    case 'transferred':
    case 'induction':
      return InductionContent
    case 'first_flush':
    case 'second_flush':
    case 'third_flush':
    case 'fourth_flush':
      return FlushContent
    case 'spent':
      return SpentContent
    default:
      return SpawnInoculationContent // Fallback
  }
}

/**
 * EventCard Component
 *
 * @param {Object} props
 * @param {Object} props.event - Event data
 * @param {string} props.event.type - Event type
 * @param {Date} props.event.date - Event date
 * @param {string} props.event.lcSource - LC source (for spawn inoculation)
 * @param {string} props.event.spawnSource - Spawn source (for substrate inoculation)
 * @param {number} props.event.quantity - Quantity (bags/blocks)
 * @param {string} props.event.format - Format (e.g., "1KG", "2.5KG")
 * @param {number} props.event.mass - Total mass in KG
 * @param {number} props.event.harvestEstimate - Estimated harvest in KG (for induction)
 * @param {number} props.event.harvested - Actual harvest in KG (for flush)
 * @param {number} props.event.yieldPercent - Yield percentage (for flush)
 * @param {number} props.event.cumulative - Cumulative harvest in KG (for flush)
 * @param {number} props.event.totalHarvest - Total harvest in KG (for spent)
 * @param {number} props.event.finalYield - Final yield percentage (for spent)
 * @param {number} props.event.flushCount - Number of flushes (for spent)
 * @param {Date} props.event.phaseEndDate - End date of the phase
 * @param {Date} props.event.batchStartDate - Batch start date (for spent lifespan)
 * @param {Object} props.batch - Batch information
 * @param {string} props.batch.id - Batch code
 * @param {string} props.batch.strain - Strain name
 * @param {Object} props.position - Popup positioning
 * @param {number} props.position.x - Horizontal center position
 * @param {number} props.position.y - Vertical position (below/above target)
 * @param {number} props.position.targetTop - Top of target element
 * @param {boolean} props.position.flipped - True if popup is above target
 * @param {Function} props.onClose - Close handler
 */
export default function EventCard({
  event,
  batch,
  position,
  onClose
}) {
  const navigate = useNavigate()
  const popupRef = useRef(null)
  const [popupHeight, setPopupHeight] = useState(200) // Smaller default for new compact layout

  // Measure actual popup height after render
  useLayoutEffect(() => {
    if (popupRef.current) {
      setPopupHeight(popupRef.current.offsetHeight)
    }
  }, [event.type]) // Re-measure when event type changes

  const popupWidth = 280 // Slightly narrower
  const popupLeft = Math.max(8, Math.min(position.x - 140, window.innerWidth - 288))
  const isFlipped = position.flipped

  // Arrow extends ~10px below the popup (14px square rotated 45deg)
  const arrowExtension = 10

  const popupTop = isFlipped
    ? Math.max(8, position.targetTop - popupHeight - arrowExtension)
    : Math.min(position.y, window.innerHeight - popupHeight - 8)

  // Calculate arrow position relative to popup left edge
  const arrowLeft = Math.max(16, Math.min(position.x - popupLeft, popupWidth - 16))

  // Get the appropriate content component
  const ContentComponent = getEventContent(event.type)

  return (
    <>
      {/* Backdrop to close popup */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Arrow pointing to target */}
      <div
        className="fixed z-[51] bg-white rotate-45"
        style={{
          width: 14,
          height: 14,
          left: popupLeft + arrowLeft - 7,
          ...(isFlipped
            ? {
                top: popupTop + popupHeight - 7,
                borderRight: '1px solid black',
                borderBottom: '1px solid black',
                boxShadow: '2px 2px 2px -1px rgba(0,0,0,0.05)'
              }
            : {
                top: popupTop - 7,
                borderLeft: '1px solid black',
                borderTop: '1px solid black',
                boxShadow: '-1px -1px 2px -1px rgba(0,0,0,0.05)'
              })
        }}
        aria-hidden="true"
      />

      {/* Popup card */}
      <div
        ref={popupRef}
        className="fixed z-50 bg-white border border-black rounded-lg shadow-lg p-4"
        style={{
          left: popupLeft,
          top: popupTop,
          width: popupWidth
        }}
        role="dialog"
        aria-label="Event details"
      >
        {/* Event-specific content */}
        <ContentComponent event={event} batch={batch} onNavigate={navigate} />
      </div>
    </>
  )
}
